'use client';

import { ColumnDef } from '@tanstack/react-table';
import {
  CalendarIcon,
  CircleDashed,
  Package,
  ShoppingCart,
  Ellipsis,
  CreditCard,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { DataTableCheckbox } from '@/components/data-table/data-table-checkbox';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatMoney } from "@/lib/money";
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import { OrderStatusBadge } from '@/components/orders/order-status-badge';
import { CreatedFromBadge } from '@/components/orders/created-from-badge';
import { Order } from '@tagadapay/crm-entities';

import { DateDisplay } from '@/components/date-display';
import { CardBadge } from '@/components/payments/card-badge';
import { PaymentMethodCell } from '@/components/payments/payment-method-cell';
import { useOrgNavigate } from '@/hooks/use-app-navigate';

// Component for org-aware navigation links
function ViewDetailsLink({ orderId, label, className }: { orderId: string; label: string; className?: string }) {
  const { orgRoutes } = useOrgNavigate();
  return <Link to={orgRoutes.ORDER_DETAILS(orderId)} className={className}>{label}</Link>;
}


interface GetOrdersTableColumnsProps {
  onRefundOrder: (orderId: string) => void;
  onSyncOrder: (orderId: string) => void;
  isRefunding?: boolean;
  isSyncing?: boolean;
  options: {
    'orders.status': string[];
    'stores.id': { id: string; name: string }[];
  };
  t: (key: string) => string;
  /** Store IDs with an enabled Shopify/Woo integration — hide Sync otherwise (#TAG-1870). */
  syncableStoreIds?: Set<string>;
}

/**
 * Get table columns for orders with consistent badges and styling
 */
export function getOrdersTableColumns({
  onRefundOrder,
  onSyncOrder,
  isRefunding = false,
  isSyncing = false,
  options,
  t,
  syncableStoreIds,
}: GetOrdersTableColumnsProps): ColumnDef<Order.Order>[] {
  return [
    // 1. Select checkbox
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label={t('table.selectAll')}
          className="translate-y-0.5"
        />
      ),
      cell: ({ row }) => (
        <DataTableCheckbox row={row} />
      ),
      enableSorting: false,
      enableHiding: false,
      size: 40,
    },
    // 2. Order Total
    {
      id: 'orderTotal',
      accessorFn: (row) => row.summaries?.[0]?.totalAdjustedAmount || 0,
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.orderTotal')} />,
      cell: ({ row }) => {
        const order = row.original;
        const amount = order.summaries?.[0]?.totalAdjustedAmount || 0;
        const currency = order.summaries?.[0]?.currency || order.currency || 'USD';

        return (
          <div className="flex flex-col gap-0.5">
            <div className="text-sm font-semibold tabular-nums">
              {formatMoney(amount, currency)}
            </div>
            {currency !== 'USD' && (
              <div className="text-muted-foreground text-xs font-medium">{currency}</div>
            )}
          </div>
        );
      },
      meta: {
        label: t('table.columns.orderTotal'),
        variant: 'money',
        icon: () => <Package />,
        options: [
          { label: t('table.filters.moneyOptions.isEqualTo'), value: 'is-equal-to' },
          { label: t('table.filters.moneyOptions.isBetween'), value: 'is-between' },
          { label: t('table.filters.moneyOptions.isGreaterThan'), value: 'is-greater-than' },
          { label: t('table.filters.moneyOptions.isLessThan'), value: 'is-less-than' },
        ],
        formatValue: (minorUnits: number) => formatMoney(minorUnits, 'USD'),
        currency: 'USD',
      },
      filterFn: () => true, // Passthrough since filtering is manual/server-side
      enableColumnFilter: true,
      enableSorting: true,
      size: 120,
    },
    // 3. Paid Amount
    {
      id: 'paidAmount',
      accessorKey: 'paidAmount',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.paidAmount')} />,
      cell: ({ row }) => {
        const order = row.original;
        const amount = order.paidAmount;
        const currency = order.currency || 'USD';

        if (!amount) {
          return <span className="text-muted-foreground">—</span>;
        }

        return (
          <div className="flex flex-col gap-0.5">
            <div className="text-sm font-semibold tabular-nums">
              {formatMoney(amount, currency)}
            </div>
            {currency !== 'USD' && (
              <div className="text-muted-foreground text-xs font-medium">{currency}</div>
            )}
          </div>
        );
      },
      enableColumnFilter: false,
      enableSorting: true,
      size: 120,
    },
    // 4. Customer
    {
      id: 'customer',
      accessorKey: 'customer.email',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.customer')} />,
      cell: ({ row }) => {
        const order = row.original;
        const fullName = `${order.customer.firstName || ''} ${order.customer.lastName || ''}`.trim();
        const displayName = fullName || '—';
        const email = order.customer.email || '—';

        return (
          <div className="flex min-w-0 flex-col gap-0.5">
            <div className="truncate text-sm font-medium">{displayName}</div>
            <div className="text-muted-foreground truncate text-xs">{email}</div>
          </div>
        );
      },
      meta: {
        label: t('table.columns.customer'),
      },
      enableColumnFilter: false, // Use search bar for customer search (full-text search)
      enableSorting: false,
      size: 200,
    },
    // 5. Status
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.status')} />,
      cell: ({ row }) => {
        const order = row.original;
        return <OrderStatusBadge status={order.status} />;
      },
      meta: {
        label: t('table.columns.status'),
        variant: 'multiSelect',
        options: options['orders.status'].map((status) => ({
          label: t(`table.filters.status.${status}`),
          value: status,
        })),
        icon: () => <CircleDashed />,
      },
      enableColumnFilter: true,
    },
    // 6. Payment Method
    {
      id: 'cardLast4',
      accessorKey: 'payment',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.paymentMethod')} />,
      cell: ({ row }) => {
        const payment = (row.original as any).payment;
        // On-chain crypto has no card: `Crypto`, the coin and network, Test on a testnet.
        if (payment?.cryptoOnchain) return <PaymentMethodCell payment={payment} />;
        const card = payment?.paymentInstrument?.card;
        return <CardBadge brand={card?.brand} last4={card?.last4} />;
      },
      meta: {
        label: t('table.columns.paymentMethod'),
        variant: 'text',
        placeholder: t('table.filters.last4Placeholder'),
        icon: () => <CreditCard />,
        filterWidth: 'w-32',
      },
      enableColumnFilter: true,
      enableSorting: false,
      enableHiding: true,
    },
    // 7. Created At
    {
      id: 'createdAt',
      accessorKey: 'createdAt',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.date')} />,
      cell: ({ row }) => (
        <DateDisplay
          value={row.original.createdAt}
          mode="split"
          className="text-sm font-medium tabular-nums"
        />
      ),
      meta: {
        label: t('table.columns.date'),
        variant: 'dateRange',
        icon: () => <CalendarIcon />,
      },
      enableColumnFilter: true,
      enableSorting: true,
      size: 130,
    },
    // 8. Store
    {
      id: 'store',
      accessorKey: 'store.name',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.store')} />,
      cell: ({ row }) => {
        const order = row.original;
        const storeName = order.store?.name;

        if (!storeName) {
          return <span className="text-muted-foreground text-sm">—</span>;
        }

        return (
          <div className="flex items-center gap-1.5">
            <div className="truncate text-sm font-medium">{storeName}</div>
          </div>
        );
      },
      meta: {
        label: t('table.columns.store'),
        variant: 'multiSelect',
        options: options['stores.id'].map((store) => ({
          label: store.name,
          value: store.id,
        })),
        icon: () => <ShoppingCart />,
      },
      enableColumnFilter: true,
      enableHiding: true,
      size: 150,
    },
    // 9. Source
    {
      id: 'createdFrom',
      accessorKey: 'createdFrom',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.source')} />,
      cell: ({ row }) => {
        const createdFrom = row.original.createdFrom;
        if (!createdFrom) {
          return <span className="text-muted-foreground text-sm">—</span>;
        }
        return <CreatedFromBadge createdFrom={createdFrom} />;
      },
      meta: {
        label: t('table.columns.source'),
        variant: 'multiSelect',
        options: [
          { label: t('table.filters.sourceOptions.checkout'), value: 'checkoutSession' },
          { label: t('table.filters.sourceOptions.manual'), value: 'manual' },
          { label: t('table.filters.sourceOptions.upsell'), value: 'upsellSession' },
          { label: t('table.filters.sourceOptions.rebill'), value: 'rebill' },
        ],
      },
      enableColumnFilter: true,
      enableSorting: false,
      enableHiding: true,
    },
    // 10. External ID (hidden by default, copiable)
    {
      id: 'externalOrderId',
      accessorKey: 'externalOrderId',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.externalId')} />,
      cell: ({ row }) => {
        const externalId = row.original.externalOrderId;

        if (!externalId) {
          return <span className="text-muted-foreground text-sm">—</span>;
        }

        return (
          <CopyableResourceBadge
            name={t('table.columns.externalId')}
            value={externalId.length > 12 ? `${externalId.substring(0, 12)}...` : externalId}
            copyValue={externalId}
          />
        );
      },
      meta: {
        label: t('table.columns.externalOrderId'),
      },
      enableColumnFilter: false, // Use main search bar (searches external ID)
      enableSorting: false,
      enableHiding: true,
      size: 180,
    },
    // 11. Order ID (copyable, at the end)
    {
      id: 'orderNumber',
      accessorKey: 'orderNumber',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.orderNumber')} />,
      cell: ({ row }) => {
        const order = row.original;
        const displayValue = `${order.orderNumber ?? '-'}`;
        return (
          <CopyableResourceBadge
            name={t('table.columns.orderNumber')}
            value={displayValue}
            copyValue={order.orderNumber?.toString() ?? ''}
          />
        );
      },
      enableSorting: true,
      enableHiding: true,
      size: 140,
    },
    {
      id: 'id',
      accessorKey: 'id',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.orderId')} />,
      cell: ({ row }) => {
        const order = row.original;
        const displayValue = `${order.id.substring(0, 8)}...`;
        return (
          <CopyableResourceBadge
            name={t('table.columns.orderId')}
            value={displayValue}
            copyValue={order.id}
          />
        );
      },
      enableSorting: true,
      enableHiding: true,
      size: 140,
    },
    // 12. Actions
    {
      id: 'actions',
      cell: ({ row }) => {
        const order = row.original;
        const storeId = order.store?.id;
        const canSyncToProvider =
          !!storeId && (syncableStoreIds?.has(storeId) ?? false) && !order.externalOrderId;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                aria-label={t('table.openMenu')}
                variant="ghost"
                className="data-[state=open]:bg-muted flex h-8 w-8 p-0"
              >
                <Ellipsis className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[160px]">
              <DropdownMenuItem asChild>
                <ViewDetailsLink orderId={order.id} label={t('table.actions.viewDetails')} />
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {canSyncToProvider ? (
                <DropdownMenuItem onClick={() => onSyncOrder(order.id)} disabled={isSyncing}>
                  {isSyncing ? t('table.actions.syncing') : t('table.actions.syncToProvider')}
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem>{t('table.actions.printInvoice')}</DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onRefundOrder(order.id)}
                disabled={isRefunding}
                className="text-destructive focus:text-destructive"
              >
                {isRefunding ? t('table.actions.refunding') : t('table.actions.refundOrder')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
      enableSorting: false,
      enableHiding: false,
      size: 40,
    },
  ];
}
